import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import clsx from 'clsx';
import api, { apiErrorMessage } from '../services/api';
import ShareModal from '../components/ShareModal';

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = React.useContext(AuthContext);

  const [event, setEvent] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAttending, setIsAttending] = useState(false);
  const [comment, setComment] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [rsvpHover, setRsvpHover] = useState(false);
  const [timeToJoin, setTimeToJoin] = useState('');

  // Countdown timer effect
  useEffect(() => {
    if (!event) return;

    const updateCountdown = () => {
      const startTime = new Date(event.StartTime || event.start_time);
      const now = new Date();
      const timeDiff = startTime.getTime() - now.getTime();

      if (timeDiff > 0) {
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) {
          setTimeToJoin(`${days}d ${hours}h ${minutes}m`);
        } else if (hours > 0) {
          setTimeToJoin(`${hours}h ${minutes}m`);
        } else if (minutes > 0) {
          setTimeToJoin(`${minutes}m`);
        } else {
          setTimeToJoin('Less than 1m');
        }
      } else {
        setTimeToJoin('Event has started');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [event]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/events/${id}`);
        // Unwrap the standard { success, message, data } response envelope.
        const body = response.data;
        const eventData = (body && body.data) ? body.data : (body && body.event ? body.event : body);

        const attendeeList = eventData.Attendees || eventData.attendees || [];

        setEvent(eventData);
        setComments(eventData.Comments || eventData.comments || []);

        if (currentUser) {
          setIsAttending(attendeeList.some(attendee => (attendee.ID || attendee.id) === currentUser.id));
        } else {
          setIsAttending(false);
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to load event:', err);
        // Rejections are APIError instances, which carry `.status`/`.code` and
        // have no `.response` -- the old `if (err.response)` branch never ran,
        // so every failure showed the same generic message.
        let errorMessage = 'Failed to load event details';

        if (err.status === 404) {
          errorMessage = 'This CliQQ no longer exists. It may have been deleted or the link is out of date.';
        } else if (err.status === 401) {
          errorMessage = 'You need to be logged in to view this event.';
        } else if (err.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.code === 'NETWORK_ERROR' || err.code === 'TIMEOUT_ERROR') {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = apiErrorMessage(err, errorMessage);
        }

        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, currentUser]);

  const handleAttend = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      if (isAttending) {
        await api.delete(`/api/events/${id}/register`);
        setIsAttending(false);
        if (event) {
          setEvent({
            ...event,
            CurrentAttendees: (event.CurrentAttendees || 0) - 1
          });
        }
        toast.success('You have left this event.');
      } else {
        await api.post(`/api/events/${id}/register`);
        setIsAttending(true);
        if (event) {
          setEvent({
            ...event,
            CurrentAttendees: (event.CurrentAttendees || 0) + 1
          });
        }
        toast.success('Successfully joined!');
      }
    } catch (err) {
      console.error('Failed to update attendance:', err);
      toast.error('Something went wrong. Please try again.');
      setIsAttending(!isAttending);
      if (event) {
        setEvent({
          ...event,
          CurrentAttendees: isAttending
            ? (event.CurrentAttendees || 0) + 1
            : (event.CurrentAttendees || 0) - 1
        });
      }
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!comment.trim()) return;

    try {
      const response = await api.post(`/api/events/${id}/comments`, { content: comment });
      const body = response.data;
      const created = (body && body.data) ? body.data : body;
      // The create response has no preloaded author; attach the current user so
      // the new comment renders with the right name/avatar immediately.
      const withUser = {
        ...created,
        User: created.User || created.user || {
          username: currentUser.username || currentUser.Username,
          avatar: currentUser.avatar || currentUser.Avatar,
        },
      };
      setComments([...comments, withUser]);
      setComment('');
      toast.success('Comment posted!');
    } catch (err) {
      console.error('Failed to submit comment:', err);
      toast.error(
        err.status === 401
          ? 'Your session has expired. Please log in again.'
          : apiErrorMessage(err, 'Failed to post comment. Please try again.')
      );
    }
  };

  // --- Loading State ---
  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <div className="text-center py-20">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent mb-4" />
          <p className="text-surface-500 text-body">Loading event details...</p>
        </div>
      </div>
    );
  }

  // --- Error State ---
  if (error) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-soft-md p-8 text-center max-w-md w-full"
        >
          <div className="w-14 h-14 rounded-2xl bg-error-50 text-error-500 flex items-center justify-center mx-auto mb-5">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-h2 text-surface-900 mb-2">Error Loading Event</h2>
          <p className="text-body text-surface-500 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-5 py-2.5 bg-surface-100 text-surface-700 text-body-sm font-semibold rounded-xl hover:bg-surface-200 transition-colors"
            >
              Go Home
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // --- Not Found ---
  if (!event) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-soft-md p-8 text-center max-w-md w-full">
          <h2 className="text-h2 text-surface-900 mb-2">Event Not Found</h2>
          <p className="text-body text-surface-500">The event you are looking for does not exist.</p>
        </div>
      </div>
    );
  }

  // --- Normalize field names ---
  const imageUrl = event.ImageURL || event.image_url;
  const title = event.Title || event.title || 'Untitled Event';
  const startTime = event.StartTime || event.start_time;
  const endTime = event.EndTime || event.end_time;
  const location = event.Location || event.location || '';
  const city = event.City || event.city || '';
  const state = event.State || event.state || '';
  const description = event.Description || event.description || '';
  const currentAttendees = event.CurrentAttendees || event.current_attendees || 0;
  const maxAttendees = event.MaxAttendees || event.max_attendees;
  const host = event.Host || event.host || {};
  const hostName = host?.Username || host?.username || 'Event Host';
  const hostAvatar = host?.Avatar || host?.avatar;

  const secondaryLocation = event.SecondaryLocation || event.secondary_location;
  const secondaryAddress = event.SecondaryAddress || event.secondary_address;
  const secondaryTime = event.SecondaryTime || event.secondary_time;
  const secondaryDescription = event.SecondaryDescription || event.secondary_description;

  const rawCategories = event.Category || event.category;
  const categories = rawCategories
    ? (Array.isArray(rawCategories)
        ? rawCategories.map(c => (typeof c === 'object' && c !== null ? (c.name || c.Name || '') : String(c || '')))
        : [typeof rawCategories === 'object' && rawCategories !== null ? (rawCategories.name || rawCategories.Name || '') : String(rawCategories || '')]
      ).filter(Boolean)
    : [];

  const attendeeList = event?.Attendees || event?.attendees || [];

  // Date helpers
  const eventDate = startTime ? new Date(startTime) : null;
  const monthAbbr = eventDate ? eventDate.toLocaleString('en-US', { month: 'short' }).toUpperCase() : '';
  const dayNumber = eventDate ? eventDate.getDate() : '';
  const dateString = eventDate
    ? eventDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  const timeString = eventDate
    ? eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    : '';
  const endTimeString = endTime
    ? new Date(endTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    : '';
  const displayTime = endTimeString ? `${timeString} - ${endTimeString}` : timeString;

  const displayLocation = [location, city, state].filter(Boolean).join(', ');
  const capacityLabel = maxAttendees ? `${currentAttendees} / ${maxAttendees}` : `${currentAttendees} going`;

  // Avatar gradient colors
  const avatarGradients = [
    'from-primary-300 to-primary-500',
    'from-accent-300 to-accent-500',
    'from-primary-400 to-accent-400',
    'from-accent-400 to-primary-300',
  ];

  // Host initial for avatar fallback
  const hostInitial = hostName.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-surface-50">
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative rounded-2xl overflow-hidden mb-8"
        >
          <div className="event-hero aspect-[16/9]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary-300 via-primary-500 to-accent-300" />
            )}

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

            {/* Date badge - top left */}
            {eventDate && (
              <div className="absolute top-4 left-4 flex flex-col items-center bg-white/85 backdrop-blur-sm rounded-xl px-3 py-2 shadow-soft-sm">
                <span className="text-[10px] font-bold text-primary-600 leading-tight tracking-wide">
                  {monthAbbr}
                </span>
                <span className="text-xl font-bold text-surface-900 leading-tight">
                  {dayNumber}
                </span>
              </div>
            )}

            {/* Title overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h1 className="text-white text-h1 drop-shadow-lg mb-1">{title}</h1>
              {dateString && (
                <p className="text-white/80 text-body">
                  {dateString}{displayTime ? ` \u00B7 ${displayTime}` : ''}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Info Cards Row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8"
        >
          {/* Date & Time */}
          <div className="bg-white rounded-xl shadow-soft-sm border border-surface-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-caption text-surface-400 uppercase tracking-wide">Date & Time</p>
                <p className="text-body-sm font-semibold text-surface-900 truncate">
                  {eventDate ? eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'TBD'}
                </p>
                {displayTime && (
                  <p className="text-caption text-surface-500">{displayTime}</p>
                )}
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white rounded-xl shadow-soft-sm border border-surface-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-success-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-caption text-surface-400 uppercase tracking-wide">Location</p>
                <p className="text-body-sm font-semibold text-surface-900 truncate">
                  {displayLocation || 'TBD'}
                </p>
              </div>
            </div>
          </div>

          {/* Capacity */}
          <div className="bg-white rounded-xl shadow-soft-sm border border-surface-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-warning-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-caption text-surface-400 uppercase tracking-wide">Capacity</p>
                <p className="text-body-sm font-semibold text-surface-900">{capacityLabel}</p>
                {timeToJoin && (
                  <p className="text-caption text-surface-500">Starts in {timeToJoin}</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Host Section */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="bg-white rounded-xl shadow-soft-sm border border-surface-200 p-5 mb-6"
        >
          <div className="flex items-center gap-4">
            {hostAvatar ? (
              <img
                src={hostAvatar}
                alt={hostName}
                className="w-12 h-12 rounded-full object-cover border-2 border-surface-100"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-300 to-primary-500 flex items-center justify-center border-2 border-surface-100 flex-shrink-0">
                <span className="text-white text-body font-bold">{hostInitial}</span>
              </div>
            )}
            <div>
              <p className="text-caption text-surface-400">Organized by</p>
              <p className="text-body font-semibold text-surface-900">{hostName}</p>
            </div>

            {/* Action buttons right-aligned */}
            <div className="ml-auto flex items-center gap-2">
              {currentUser && host && (currentUser.id === (host.ID || host.id)) && (
                <button
                  onClick={() => navigate(`/events/${id}/edit`)}
                  className="px-4 py-2 border border-surface-200 text-surface-600 text-body-sm font-medium rounded-xl hover:bg-surface-50 transition-colors"
                >
                  Edit
                </button>
              )}
              <button
                onClick={() => setShowShareModal(true)}
                className="p-2 border border-surface-200 text-surface-500 rounded-xl hover:bg-surface-50 transition-colors"
                aria-label="Share event"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Attendees & RSVP Section */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white rounded-xl shadow-soft-sm border border-surface-200 p-5 mb-6"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              {/* Overlapping avatar stack */}
              <div className="flex -space-x-2">
                {attendeeList.slice(0, 4).map((attendee, i) => {
                  const avatar = attendee.Avatar || attendee.avatar;
                  const name = attendee.Username || attendee.username || '';
                  const gradient = avatarGradients[i % avatarGradients.length];
                  return (
                    <div
                      key={attendee.ID || attendee.id || i}
                      className={clsx(
                        'w-9 h-9 rounded-full border-2 border-white flex items-center justify-center',
                        !avatar && `bg-gradient-to-br ${gradient}`
                      )}
                      title={name}
                    >
                      {avatar ? (
                        <img src={avatar} alt={name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-[11px] font-bold text-white">
                          {name.charAt(0).toUpperCase() || String.fromCharCode(65 + i)}
                        </span>
                      )}
                    </div>
                  );
                })}
                {currentAttendees > 4 && (
                  <div className="w-9 h-9 rounded-full border-2 border-white bg-surface-200 flex items-center justify-center">
                    <span className="text-[11px] font-bold text-surface-600">+{currentAttendees - 4}</span>
                  </div>
                )}
              </div>
              <span className="text-body-sm font-medium text-surface-600">
                {currentAttendees} going
              </span>
            </div>

            {/* RSVP Button - Three states */}
            {currentUser ? (
              isAttending ? (
                <button
                  onClick={handleAttend}
                  onMouseEnter={() => setRsvpHover(true)}
                  onMouseLeave={() => setRsvpHover(false)}
                  className={clsx(
                    'px-6 py-2.5 rounded-xl text-body-sm font-semibold transition-all duration-200 border-2',
                    rsvpHover
                      ? 'border-accent-500 text-accent-600 bg-accent-50'
                      : 'border-success-500 text-success-600 bg-success-50'
                  )}
                >
                  {rsvpHover ? (
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Leave
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Going
                    </span>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleAttend}
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-xl text-body-sm font-semibold hover:bg-primary-600 shadow-colored transition-all duration-200"
                >
                  Join CliQQ
                </button>
              )
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2.5 bg-primary-500 text-white rounded-xl text-body-sm font-semibold hover:bg-primary-600 shadow-colored transition-all duration-200"
              >
                Join CliQQ
              </button>
            )}
          </div>
        </motion.div>

        {/* Description Section */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
          className="bg-white rounded-xl shadow-soft-sm border border-surface-200 p-6 mb-6"
        >
          <h2 className="text-h3 text-surface-900 mb-3">About this CliQQ</h2>
          <p className="text-body text-surface-600 leading-relaxed whitespace-pre-line">
            {description || 'No description provided.'}
          </p>
        </motion.div>

        {/* Categories / Tags Section - only show if there are actual categories */}
        {categories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="mb-6"
          >
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <span
                  key={cat}
                  className="event-tab-button inline-flex items-center px-3.5 py-1.5 rounded-xl bg-primary-50 text-primary-700 text-body-sm font-medium"
                >
                  {cat}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Secondary Meetup Information */}
        {secondaryLocation && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35 }}
            className="bg-white rounded-xl shadow-soft-sm border border-surface-200 p-6 mb-6"
          >
            <h3 className="text-h3 text-surface-900 mb-4">Secondary Meetup</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-caption text-surface-400 uppercase tracking-wide mb-1">Location</p>
                <p className="text-body font-medium text-surface-900">{secondaryLocation}</p>
                {secondaryAddress && (
                  <p className="text-body-sm text-surface-500 mt-0.5">{secondaryAddress}</p>
                )}
              </div>
              {secondaryTime && (
                <div>
                  <p className="text-caption text-surface-400 uppercase tracking-wide mb-1">Time</p>
                  <p className="text-body font-medium text-surface-900">
                    {new Date(secondaryTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at{' '}
                    {new Date(secondaryTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </p>
                </div>
              )}
            </div>
            {secondaryDescription && (
              <div className="mt-4">
                <p className="text-caption text-surface-400 uppercase tracking-wide mb-1">Details</p>
                <p className="text-body text-surface-600">{secondaryDescription}</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Comments Section */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="bg-white rounded-xl shadow-soft-sm border border-surface-200 p-6"
        >
          <h2 className="text-h3 text-surface-900 mb-5">
            Comments
            <span className="text-body-sm text-surface-400 font-normal ml-2">({comments.length})</span>
          </h2>

          {/* Comment Form */}
          {currentUser && (
            <form onSubmit={handleCommentSubmit} className="mb-6">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Leave a comment..."
                className="w-full p-3.5 border border-surface-200 rounded-xl text-body text-surface-800 placeholder-surface-400 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 resize-none transition-colors"
                rows={3}
              />
              <button
                type="submit"
                disabled={!comment.trim()}
                className={clsx(
                  'mt-2 px-5 py-2.5 rounded-xl text-body-sm font-semibold transition-colors',
                  comment.trim()
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-surface-100 text-surface-400 cursor-not-allowed'
                )}
              >
                Post Comment
              </button>
            </form>
          )}

          {/* Comments List */}
          {comments.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-2xl bg-surface-100 text-surface-400 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-body text-surface-500">No comments yet. Be the first to comment!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((c) => {
                const commentAvatar = c.User?.Avatar || c.User?.avatar;
                const commentUsername = c.User?.Username || c.User?.username || 'Anonymous';
                const commentInitial = commentUsername.charAt(0).toUpperCase();
                return (
                  <div key={c.ID || c.id} className="flex gap-3">
                    {commentAvatar ? (
                      <img
                        src={commentAvatar}
                        alt={commentUsername}
                        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-200 to-primary-400 flex items-center justify-center flex-shrink-0">
                        <span className="text-[11px] font-bold text-white">{commentInitial}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="bg-surface-50 rounded-xl p-3.5">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-body-sm font-semibold text-surface-900">{commentUsername}</span>
                          <span className="text-caption text-surface-400">
                            {(c.created_at || c.CreatedAt)
                              ? new Date(c.created_at || c.CreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : ''}
                          </span>
                        </div>
                        <p className="text-body text-surface-700">{c.Content || c.content}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

      </div>

      <ShareModal
        event={event}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </div>
  );
}

export default EventDetail;
